import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  sellerService,
  tileService,
  accountService,
} from "../../services/enhancedCrudService";
import { apiService } from "../../services/apiService";
import Select from "react-select";

const SELLER_PLACE_CHOICES = [
  { value: "morbi", label: "Morbi" },
  { value: "than", label: "Than" },
  { value: "imported", label: "Imported" },
];

const SELLER_TYPES = [
  { value: "manufacturer", label: "Manufacturer" },
  { value: "importer", label: "Importer" },
  { value: "stock_premium", label: "Stockiest Only Premium" },
  { value: "stock_other", label: "Stockiest Other Grade" },
];

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [tileTypes, setTileTypes] = useState([]);
  const [tileSizes, setTileSizes] = useState([]);
  const [sellerPlaces, setSellerPlaces] = useState([]);
  const [selectedTileType, setSelectedTileType] = useState(null);
  const [selectedTileSize, setSelectedTileSize] = useState(null);
  const [selectedSellerPlace, setSelectedSellerPlace] = useState(null);
  const [selectedSellerType, setSelectedSellerType] = useState(null);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [userData, setUserData] = useState({
    firm_name: "",
    mobile_number: "",
    inquiry_mobile_number: "",
    category: "",
    gst_number: "",
    address_line: "",
    road: "",
    city: "",
    district: "",
    district_id: "",
    state: "",
    state_id: "",
    pin_code: "",
    tile_type: "",
    seller_place: "",
    tile_size: "",
    seller_type: "",
  });

  const fetchCategories = async () => {
    try {
      const response = await sellerService.getCategoryList();
      if (response) {
        const categoryOptions = response.map((category) => ({
          value: category.name,
          label: category.name,
        }));
        setCategories(categoryOptions);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await tileService.getTypes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setTileTypes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  const fetchSizes = async () => {
    try {
      const response = await tileService.getSizes();
      if (response.types && response.types.length) {
        let mapTyped = response.types.map((row) => {
          return {
            label: row.name,
            value: row.id,
          };
        });
        setTileSizes(mapTyped);
      }
    } catch (error) {
      console.error("Error fetching sizes:", error);
    }
  };

  const fetchSellerPlaces = async () => {
    try {
      setSellerPlaces(SELLER_PLACE_CHOICES);
    } catch (error) {
      console.error("Error setting seller places:", error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await accountService.getStates();
      if (response && response.length) {
        const stateOptions = response.map((state) => ({
          value: state.id,
          label: state.name,
        }));
        setStates(stateOptions);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
    }
  };

  const fetchDistricts = async (stateId) => {
    try {
      if (!stateId) {
        setDistricts([]);
        return;
      }
      const response = await accountService.getDistricts(stateId);
      if (response && response.length) {
        const districtOptions = response.map((district) => ({
          value: district.id,
          label: district.name,
        }));
        setDistricts(districtOptions);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
      toast.error("Failed to fetch districts");
      setDistricts([]);
    }
  };

  const [userDetailsData, setUserDetailsData] = useState(null);

  const bindDropdownData = (data) => {
    if (data.tile_type && data.tile_type !== null) {
      const tileTypeOption = tileTypes.find(
        (option) => option.value === data.tile_type
      );
      if (tileTypeOption) {
        const selectedOption = {
          value: tileTypeOption.value,
          label: tileTypeOption.label,
        };
        setSelectedTileType(selectedOption);
      } else {
        setSelectedTileType({ value: data.tile_type, label: data.tile_type });
      }
    } else {
      setSelectedTileType(null);
    }

    if (data.tile_size && data.tile_size !== null) {
      const tileSizeOption = tileSizes.find(
        (option) => option.value === data.tile_size
      );
      if (tileSizeOption) {
        const selectedOption = {
          value: tileSizeOption.value,
          label: tileSizeOption.label,
        };
        setSelectedTileSize(selectedOption);
      } else {
        setSelectedTileSize({ value: data.tile_size, label: data.tile_size });
      }
    } else {
      setSelectedTileSize(null);
    }

    if (data.seller_place && data.seller_place !== null) {
      const sellerPlaceOption = SELLER_PLACE_CHOICES.find(
        (option) => option.value === data.seller_place
      );
      if (sellerPlaceOption) {
        setSelectedSellerPlace({
          value: sellerPlaceOption.value,
          label: sellerPlaceOption.label,
        });
      } else {
        setSelectedSellerPlace({
          value: data.seller_place,
          label: data.seller_place,
        });
      }
    } else {
      setSelectedSellerPlace(null);
    }

    if (data.seller_type && data.seller_type !== null) {
      const sellerTypeOption = SELLER_TYPES.find(
        (option) => option.value === data.seller_type
      );
      if (sellerTypeOption) {
        setSelectedSellerType({
          value: sellerTypeOption.value,
          label: sellerTypeOption.label,
        });
      } else {
        setSelectedSellerType({
          value: data.seller_type,
          label: data.seller_type,
        });
      }
    } else {
      setSelectedSellerType(null);
    }

    // Handle state binding
    if (data.address?.state && data.address?.state_name_display) {
      const stateOption = states.find(
        (option) => option.value === data.address.state
      );
      if (stateOption) {
        setSelectedState(stateOption);
      } else {
        setSelectedState({
          value: data.address.state,
          label: data.address.state_name_display,
        });
      }
    } else {
      setSelectedState(null);
    }

    // Handle district binding
    if (data.address?.district && data.address?.district_name_display) {
      const districtOption = districts.find(
        (option) => option.value === data.address.district
      );
      if (districtOption) {
        setSelectedDistrict(districtOption);
      } else {
        setSelectedDistrict({
          value: data.address.district,
          label: data.address.district_name_display,
        });
      }
    } else {
      setSelectedDistrict(null);
    }
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await sellerService.getProfileById(id);
      if (response && response.success && response.data) {
        const data = response.data;
        setUserDetailsData(data);
        setUserData({
          firm_name: data.firm_name || "",
          mobile_number: data.mobile_number
            ? data.mobile_number.replace(/[^\d]/g, "")
            : "",
          inquiry_mobile_number: data.inquiry_mobile_number
            ? data.inquiry_mobile_number.replace(/[^\d]/g, "")
            : "",
          category: data.category || "",
          gst_number: data.gst_number || "",
          address_line: data.address?.address_line || "",
          road: data.address?.road || "",
          city: data.address?.city || "",
          district: data.address?.district || "",
          district_id: data.address?.district || "",
          state: data.address?.state || "",
          state_id: data.address?.state || "",
          pin_code: data.address?.pin_code || "",
          tile_type: data.tile_type || "",
          seller_place: data.seller_place || "",
          tile_size: data.tile_size || "",
          seller_type: data.seller_type || "",
        });
        if (data.categories && Array.isArray(data.categories)) {
          const selectedCats = data.categories.map((cat) => ({
            value: cat.name,
            label: cat.name,
          }));
          setSelectedCategories(selectedCats);
        }
        bindDropdownData(data);
      } else {
        toast.error("Failed to fetch user details");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (selectedOptions) => {
    setSelectedCategories(selectedOptions || []);
  };

  const handleTileTypeChange = (selectedOption) => {
    setSelectedTileType(selectedOption);
    setUserData((prev) => ({
      ...prev,
      tile_type: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleTileSizeChange = (selectedOption) => {
    setSelectedTileSize(selectedOption);
    setUserData((prev) => ({
      ...prev,
      tile_size: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSellerPlaceChange = (selectedOption) => {
    setSelectedSellerPlace(selectedOption);
    setUserData((prev) => ({
      ...prev,
      seller_place: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSellerTypeChange = (selectedOption) => {
    setSelectedSellerType(selectedOption);
    setUserData((prev) => ({
      ...prev,
      seller_type: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setSelectedDistrict(null); // Reset district when state changes
    setDistricts([]); // Clear districts list
    setUserData((prev) => ({
      ...prev,
      state: selectedOption ? selectedOption.label : "",
      state_id: selectedOption ? selectedOption.value : "",
      district: "",
      district_id: "",
    }));

    // Fetch districts for the selected state
    if (selectedOption) {
      fetchDistricts(selectedOption.value);
    }
  };

  const handleDistrictChange = (selectedOption) => {
    setSelectedDistrict(selectedOption);
    setUserData((prev) => ({
      ...prev,
      district: selectedOption ? selectedOption.label : "",
      district_id: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        firm_name: userData.firm_name,
        mobile_number: userData.mobile_number,
        inquiry_mobile_number: userData.inquiry_mobile_number,
        gst_number: userData.gst_number,
        categories: selectedCategories.map((cat) => ({ name: cat.value })),
        address: {
          address_line: userData.address_line,
          road: userData.road,
          city: userData.city,
          district: userData.district_id,
          state: userData.state_id,
          pin_code: userData.pin_code,
        },
        tile_type: userData.tile_type,
        seller_place: userData.seller_place,
        tile_size: userData.tile_size,
        seller_type: userData.seller_type,
      };
      const response = await sellerService.updateProfile(id, submitData);
      if (response) {
        toast.success("Seller updated successfully");
        // Navigate back with preserved pagination state
        const returnUrl = new URLSearchParams(location.search).get('returnUrl') || '';
        navigate(`/user-list${returnUrl}`);
      } else {
        toast.error("Failed to update seller");
      }
    } catch (error) {
      console.error("Error updating seller:", error);
      toast.error("Failed to update seller");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back with preserved pagination state
    const returnUrl = new URLSearchParams(location.search).get('returnUrl') || '';
    navigate(`/user-list${returnUrl}`);
  };

  useEffect(() => {
    fetchCategories();
    fetchTypes();
    fetchSizes();
    fetchSellerPlaces();
    fetchStates();
    if (id) {
      fetchUserDetails();
    }
  }, [id]);

  useEffect(() => {
    if (
      userDetailsData &&
      tileTypes.length > 0 &&
      tileSizes.length > 0 &&
      sellerPlaces.length > 0 &&
      states.length > 0
    ) {
      bindDropdownData(userDetailsData);
    }
  }, [tileTypes, tileSizes, sellerPlaces, states, userDetailsData]);

  // Fetch districts when user data is loaded and we have a state_id
  useEffect(() => {
    if (userDetailsData?.address?.state && states.length > 0) {
      fetchDistricts(userDetailsData.address.state);
    }
  }, [userDetailsData, states]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && !userData.firm_name) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Edit Seller Information</h3>
          </div>
          <div className="col-auto">
            <button className="btn btn-secondary" onClick={handleCancel}>
              <i className="feather-arrow-left"></i> Back to List
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Seller Information</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="firm_name">Firm Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="firm_name"
                    name="firm_name"
                    value={userData.firm_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="mobile_number">Mobile Number *</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="mobile_number"
                    name="mobile_number"
                    value={userData.mobile_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="inquiry_mobile_number">Inquiry Mobile Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="inquiry_mobile_number"
                    name="inquiry_mobile_number"
                    value={userData.inquiry_mobile_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <Select
                    isMulti
                    options={categories}
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    placeholder="Select Categories"
                    isClearable
                    isSearchable
                    required
                  />
                </div>
              </div>

              {/* Category-specific fields */}
              {(() => {
                const categoryTypes = selectedCategories.map(
                  (cat) => cat.value
                );
                return (
                  categoryTypes.includes("tiles") && (
                    <>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="tile_type">Tile Type</label>
                          <Select
                            options={tileTypes}
                            value={selectedTileType}
                            onChange={handleTileTypeChange}
                            placeholder="Select Tile Type"
                            isClearable
                            isSearchable
                          />
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="tile_size">Tile Size</label>
                          <Select
                            options={tileSizes}
                            value={selectedTileSize}
                            onChange={handleTileSizeChange}
                            placeholder="Select Tile Size"
                            isClearable
                            isSearchable
                          />
                        </div>
                      </div>
                    </>
                  )
                );
              })()}
              {(() => {
                const categoryTypes = selectedCategories.map(
                  (cat) => cat.value
                );
                return (
                  categoryTypes.includes("sanitaryware") && (
                    <>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label htmlFor="seller_place">Seller Place</label>
                          <Select
                            options={sellerPlaces}
                            value={selectedSellerPlace}
                            onChange={handleSellerPlaceChange}
                            placeholder="Select Seller Place"
                            isClearable
                            isSearchable
                          />
                        </div>
                      </div>
                    </>
                  )
                );
              })()}
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="seller_type">Seller Type</label>
                  <Select
                    options={SELLER_TYPES}
                    value={selectedSellerType}
                    onChange={handleSellerTypeChange}
                    placeholder="Select Seller Type"
                    isClearable
                    isSearchable
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="gst_number">GST Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="gst_number"
                    name="gst_number"
                    value={userData.gst_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="address_line">Address Line *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="address_line"
                    name="address_line"
                    value={userData.address_line}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="road">Road *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="road"
                    name="road"
                    value={userData.road}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={userData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <Select
                    options={states}
                    value={selectedState}
                    onChange={handleStateChange}
                    placeholder="Select State"
                    isClearable
                    isSearchable
                    required
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="district">District *</label>
                  <Select
                    options={districts}
                    value={selectedDistrict}
                    onChange={handleDistrictChange}
                    placeholder="Select District"
                    isClearable
                    isSearchable
                    isDisabled={!selectedState}
                    required
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="pin_code">Pin Code *</label>
                  <input
                    type="text"
                    className="form-control"
                    id="pin_code"
                    name="pin_code"
                    value={userData.pin_code}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-group text-end mt-4">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="feather-save"></i> Update User
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                <i className="feather-x"></i> Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEdit;
